/**
 * Presentation helpers for Call Details → Client Info tab
 * (`GET call/details/:callDetailsId/client-info`).
 */

const normalizeFieldValue = value => {
    if (value == null) return "";
    const normalized = String(value).trim();
    return normalized.toLowerCase() === "null" ? "" : normalized;
};

const normalizeClientInfoFields = fields =>
    (Array.isArray(fields) ? fields : [])
        .map((field, index) => ({
            label: String(field?.label ?? "").trim(),
            value: normalizeFieldValue(field?.value),
            sensitiveKey: field?.sensitiveKey ?? null,
            key: String(field?.key ?? field?.label ?? `field-${index}`).trim(),
        }))
        .filter(field => field.label);

const objectToLabelValueFields = (sourceObject, fieldDefinitions) =>
    fieldDefinitions
        .map(({ key, label, sensitiveKey = null }) => ({
            label,
            value: normalizeFieldValue(sourceObject?.[key]),
            sensitiveKey,
            key: key || label,
        }))
        .filter(field => field.label);

export const parseCallDetailsClientInfoApiResponse = clientInfoApiResponse => {
    const payload = clientInfoApiResponse?.data ?? clientInfoApiResponse;

    if (!payload || typeof payload !== "object") {
        return null;
    }

    return payload;
};

/**
 * Normalizes API payload into a single ordered list of sections for UI `.map()`.
 * Merges `recruitingContact`, `billingContact`, `sections[]`, and `fadvLogin`.
 * First Advantage Login Details requires `canViewFadvLoginDetails` (CallViewFadvLoginDetailsAccess).
 */
export const buildCallDetailsClientInfoDisplaySections = (
    clientInfoData,
    { canViewFadvLoginDetails = false } = {}
) => {
    if (!clientInfoData || typeof clientInfoData !== "object") {
        return [];
    }

    const displaySections = [];
    const seenTitles = new Set();

    const appendSection = (title, fields) => {
        const sectionTitle = String(title ?? "").trim();
        if (!sectionTitle || seenTitles.has(sectionTitle)) {
            return;
        }

        const normalizedFields = normalizeClientInfoFields(fields);
        seenTitles.add(sectionTitle);
        displaySections.push({
            title: sectionTitle,
            fields: normalizedFields,
        });
    };

    const recruitingContact = clientInfoData.recruitingContact;
    if (recruitingContact && typeof recruitingContact === "object") {
        appendSection(
            "Recruiting Contact",
            objectToLabelValueFields(recruitingContact, [
                { key: "companyName", label: "Company Name" },
                { key: "name", label: "Name" },
            ])
        );
    }

    const billingContact = clientInfoData.billingContact;
    if (billingContact && typeof billingContact === "object") {
        appendSection(
            "Billing Contact",
            objectToLabelValueFields(billingContact, [
                { key: "terminalAddress", label: "Terminal Address" },
                { key: "entityId", label: "Entity ID" },
                {
                    key: "howManyRoutes",
                    label: "How many Routes do you own",
                },
            ])
        );
    }

    if (Array.isArray(clientInfoData.sections)) {
        clientInfoData.sections.forEach(section => {
            appendSection(
                section?.title,
                (section?.fields ?? []).map((field, index) => ({
                    label: field?.label,
                    value: field?.value,
                    key: `${section?.title ?? "section"}-${index}`,
                }))
            );
        });
    }

    const fadvLogin = clientInfoData.fadvLogin;
    if (
        canViewFadvLoginDetails &&
        fadvLogin &&
        typeof fadvLogin === "object"
    ) {
        appendSection(
            "First Advantage Login Details",
            objectToLabelValueFields(fadvLogin, [
                { key: "accountNumber", label: "Account Number" },
                { key: "loginId", label: "Login ID" },
                {
                    key: "password",
                    label: "Password",
                    sensitiveKey: "password",
                },
                {
                    key: "secretQuestion",
                    label: "Secret Question",
                    sensitiveKey: "secretQuestion",
                },
            ])
        );
    }

    return displaySections;
};

export const formatClientInfoMetricValue = value => {
    if (value === null || value === undefined || value === "") return "—";
    return String(value);
};

export const formatClientInfoTargetRange = (min, max) => {
    const hasMin = min !== null && min !== undefined && min !== "";
    const hasMax = max !== null && max !== undefined && max !== "";
    if (!hasMin && !hasMax) return "—";
    if (hasMin && hasMax) return `${min} - ${max}`;
    return String(hasMin ? min : max);
};

export const buildCallDetailsClientIncentiveMetrics = clientInfoData => {
    if (!clientInfoData || typeof clientInfoData !== "object") {
        return [];
    }

    return [
        {
            key: "plan",
            label: "Plan",
            value: formatClientInfoMetricValue(clientInfoData.plan),
            icon: "bx bx-layer",
        },
        {
            key: "target",
            label: "Target",
            value: formatClientInfoTargetRange(
                clientInfoData.incentiveTargetMin,
                clientInfoData.incentiveTargetMax
            ),
            icon: "bx bx-target-lock",
        },
        {
            key: "currentAchieved",
            label: "This Month",
            value: formatClientInfoMetricValue(clientInfoData.currentAchieved ?? 0),
            icon: "bx bx-trending-up",
        },
        {
            key: "lastAchieved",
            label: "Last Month",
            value: formatClientInfoMetricValue(clientInfoData.lastAchieved ?? 0),
            icon: "bx bx-history",
        },
    ];
};
